/**
 * Relatos crypto core — HMAC-blinded relay-level tags.
 *
 * Relays index tags, so real board/thread/event ids must never appear in tags.
 * blindTag(epochKey, id) = HMAC-SHA256(key = epochKey, msg = "relatos/blind/" || id).
 *
 * Properties the rest of the system relies on:
 * - Deterministic within an epoch: every member computes the same tag, so relay
 *   filters (#h, #e, #t) work.
 * - Unlinkable across epochs: rotating the key changes every tag.
 * - Unforgeable/uninvertible without the epoch key: a relay operator sees only
 *   random-looking hex and cannot correlate two boards or two threads.
 */
import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/hashes/utils.js';

export const BLIND_DOMAIN = 'relatos/blind/';

export function blindTag(epochKeyHex: string, id: string): string {
  return bytesToHex(hmac(sha256, hexToBytes(epochKeyHex), utf8ToBytes(BLIND_DOMAIN + id)));
}

/** Convenience: blinded board tag value (goes in the `h` tag). */
export function blindBoard(epochKeyHex: string, boardId: string): string {
  return blindTag(epochKeyHex, 'board:' + boardId);
}

/** Blinded thread tag value (goes in the `t` tag on thread members). */
export function blindThread(epochKeyHex: string, threadRootId: string): string {
  return blindTag(epochKeyHex, 'thread:' + threadRootId);
}

/** Blinded target tag value (goes in the `e` tag of reactions). */
export function blindTarget(epochKeyHex: string, eventId: string): string {
  return blindTag(epochKeyHex, 'target:' + eventId);
}
